import { ArrowsClockwise, Calendar, Check, DownloadSimple } from '@phosphor-icons/react';
import styled from 'styled-components';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { Modal, Table } from 'semantic-ui-react';
import ReactDatePicker from 'react-datepicker';
import { AppTable, TablePagination } from '../../components';
import { ColoredPill, HoverBorderButton, MuteText } from '../../elements';
import { CommonUtility, theme, ToastMessage, WaveService } from '../../utility';
import { DatepickerContainer } from '../../elements/Datepicker';

const columns = [
  {
    key: 'exported_date',
    text: 'Exported Date',
    textAlign: 'left',
    width: 3,
  },
  {
    key: 'report_type',
    text: 'Report Type',
    textAlign: 'left',
    width: 3,
  },
  {
    key: 'date_range',
    text: 'Date Range',
    textAlign: 'left',
    width: 4,
  },
  {
    key: 'order_status',
    text: 'Status',
    textAlign: 'left',
    width: 3,
  },
  {
    key: 'file',
    text: 'File',
    textAlign: 'left',
    width: 3,
  },
];

export default function WavesExportPopup({ exportPopup, setExportPopup, search }) {
  const popupRef = useRef(null);
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const startOfWeek = new Date(now.setDate(diff));
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [consolidate, setConsolidate] = useState(false);
  const [exportType, setExportType] = useState('waves-summary');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [startDate, setStartDate] = useState(startOfWeek);
  const [popupState, setPopupState] = useState(null);
  const [endDate, setEndDate] = useState(endOfDay);

  useEffect(() => {
    const fetchExportHistory = async () => {
      setLoading(true);
      try {
        const response = await WaveService.getWavesExport({
          page,
          limit,
        });
        setData(response?.data?.exports || []);
        setTotalCount(response?.totalCount);
      } catch (error) {
        ToastMessage.apiError(error);
      } finally {
        setLoading(false);
      }
    };

    if (exportPopup) fetchExportHistory();
  }, [page, limit, exportPopup, refetch]);

  const handleFetchReport = async () => {
    if (!CommonUtility.isValidDate(startDate) || !CommonUtility.isValidDate(endDate)) {
      ToastMessage.error('Start and End Date must be valid');
      return;
    }

    try {
      setFetchLoading(true);

      let params = {
        warehouseId: search?.warehouseId,
        status: search?.status,
        startDate,
        endDate,
        exportType,
      };

      if (exportType === 'waves-summary') {
        params.orderConsolidated = consolidate;
      }

      params = CommonUtility.removeEmptyFields(params);
      await WaveService.createWavesExport(params);
      setRefetch(!refetch);
      ToastMessage.success('Waves export report is being generated');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleDateChange = update => {
    const [start, end] = update;

    if (start && end) {
      if (start.toDateString() === end.toDateString()) {
        end.setHours(23, 59, 59, 999);
      }

      start.setHours(0, 0, 0, 0);
      setStartDate(new Date(start));
      setEndDate(new Date(end));
    } else {
      if (start) start.setHours(0, 0, 0, 0);
      if (end) end.setHours(23, 59, 59, 999);
      setStartDate(start);
      setEndDate(end);
    }
  };
  useEffect(() => {
    const handleClickOutside = e => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setPopupState(null);
      }
    };

    if (popupState) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupState]);

  const monthToDate = () => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    firstOfMonth.setHours(0, 0, 0, 0);
    now.setHours(23, 59, 59, 999);
    setStartDate(firstOfMonth);
    setEndDate(new Date(now));
    setPopupState(null);
  };

  const pastMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    firstDay.setHours(0, 0, 0, 0);
    lastDay.setHours(23, 59, 59, 999);
    setStartDate(firstDay);
    setEndDate(lastDay);
    setPopupState(null);
  };

  return (
    <Modal open={exportPopup} onClose={() => setExportPopup(!exportPopup)}>
      <Header>
        <h2 className="m-0">Export Waves</h2>
        <ActionsWrapper>
          <DateRangeWrapper>
            <HoverBorderButton
              onClick={() => {
                popupState ? setPopupState(null) : setPopupState('menu');
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Calendar />
                <span>Filters</span>
              </div>
            </HoverBorderButton>

            {popupState === 'menu' && (
              <FilterPopup ref={popupRef}>
                <HoverBorderButton
                  onClick={() => {
                    setStartDate(startOfWeek);
                    setEndDate(endOfDay);
                    setPopupState('calendar');
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Calendar />
                    <span>Filter Date</span>
                  </div>
                </HoverBorderButton>

                <HoverBorderButton onClick={monthToDate}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Calendar />
                    <span>Month to Date</span>
                  </div>
                </HoverBorderButton>

                <HoverBorderButton onClick={pastMonth}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Calendar />
                    <span>Past Month</span>
                  </div>
                </HoverBorderButton>
                <HoverBorderButton
                  onClick={() =>
                    setExportType(
                      exportType === 'waves-summary' ? 'shipped-orders' : 'waves-summary',
                    )
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                      width: 175,
                    }}
                  >
                    <span>
                      {exportType === 'waves-summary'
                        ? 'Wave Summary Export'
                        : 'Shipped Orders Export'}
                    </span>
                    <Check weight="bold" />
                  </div>
                </HoverBorderButton>
                {exportType === 'waves-summary' && (
                  <HoverBorderButton onClick={() => setConsolidate(!consolidate)}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        whiteSpace: 'nowrap',
                        width: 150,
                      }}
                    >
                      <span>Consolidate by Order</span>
                      {consolidate && <Check weight="bold" />}
                    </div>
                  </HoverBorderButton>
                )}
              </FilterPopup>
            )}

            {popupState === 'calendar' && (
              <DatepickerContainerStyled ref={popupRef}>
                <ReactDatePicker
                  inline
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={dates => handleDateChange(dates)}
                />
              </DatepickerContainerStyled>
            )}
          </DateRangeWrapper>

          <HoverBorderButton
            icon={<ArrowsClockwise size={16} />}
            onClick={() => handleFetchReport()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '10px',
            }}
            loading={fetchLoading}
          >
            Fetch Report
          </HoverBorderButton>

          <HoverBorderButton
            onClick={() => setRefetch(!refetch)}
            style={{
              width: '37px',
              height: '37px',
              padding: 0,
              marginLeft: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ArrowsClockwise size={16} />
          </HoverBorderButton>
        </ActionsWrapper>
      </Header>
      <div className="d-flex mx-4">
        <MuteText>
          Report will be generated from: {moment(startDate).format('MMM DD, YYYY')} to{' '}
          {endDate ? (
            moment(endDate).format('MMM DD, YYYY')
          ) : (
            <span style={{ color: theme.colors.red }}>Set end date</span>
          )}{' '}
          as{' '}
          <strong>
            {exportType === 'waves-summary' ? 'Wave Summary' : 'Shipped Orders'}
          </strong>{' '}
          for <strong>{CommonUtility.toTitleCase(search.status)}</strong> waves
        </MuteText>
      </div>
      <TableWrapper>
        <AppTable
          tableKey="scm-storage-provider-table-popup"
          data={data}
          columns={columns}
          loading={loading}
          noShadow
          RowComponent={<Row />}
          wrapperClass="mt-0"
        />
      </TableWrapper>
      <div className="mx-4">
        <TablePagination
          noPadding
          colSpan={columns.length}
          currentPage={page}
          totalPages={Math.ceil(totalCount / limit)}
          pageChanged={setPage}
          pageSizeChanged={setLimit}
          pageSize={limit}
          totalData={totalCount}
        />
      </div>

      <StickyFooter>
        <HoverBorderButton
          onClick={() => {
            setStartDate(startOfWeek);
            setEndDate(endOfDay);
            setExportPopup(!exportPopup);
          }}
          style={{ width: '100px' }}
        >
          Close
        </HoverBorderButton>
      </StickyFooter>
    </Modal>
  );
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 20px;
`;

const ActionsWrapper = styled.div`
  display: flex;
`;

const DateRangeWrapper = styled.div`
  position: relative;
`;

const FilterPopup = styled.div`
  position: absolute;
  top: 110%;
  left: 0;
  z-index: 999;
  background: white;
  padding: 12px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TableWrapper = styled.div`
  padding: 0 20px;
  margin-bottom: 0px;
  max-height: 50vh;
  overflow-y: auto;
  margin-top: 10px;
`;

const StickyFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px;
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 10;
`;

const DatepickerContainerStyled = styled(DatepickerContainer)`
  position: absolute;
  top: 110%;
  left: 0;
  z-index: 999;
`;

function Row({ item }) {
  const [loading, setLoading] = useState(false);
  const exportType = getExportType(item);

  const handleDownload = async item => {
    setLoading(true);

    try {
      if (!item?.downloadUrl) {
        ToastMessage.error('No file available for download');
        return;
      }
      const response = await fetch(item.downloadUrl);
      const blob = await response.blob();

      const fileName = (
        item?.results?.messages?.filename || 'Waves-Summary-Export'
      ).replace('.csv', '');

      CommonUtility.downloadCSV(blob, fileName);
      ToastMessage.success('File downloaded successfully');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Table.Row>
      <Table.Cell>
        {item?.createdAt ? (
          <>
            {moment(item.createdAt).format('MMM DD, YYYY')}
            <br />
            <MuteText>{moment(item.createdAt).format('hh:mm A')}</MuteText>
          </>
        ) : (
          'N/A'
        )}
      </Table.Cell>

      <Table.Cell>{formatExportType(exportType)}</Table.Cell>

      <Table.Cell>
        {item?.startTime && item?.endTime ? (
          <>
            {moment(item.startTime).format('MMM DD')}–
            {moment(item.endTime).format(
              moment(item.startTime).isSame(item.endTime, 'month')
                ? 'DD, YYYY'
                : 'MMM DD, YYYY',
            )}
          </>
        ) : (
          ''
        )}
      </Table.Cell>

      <Table.Cell>
        <ColoredPill
          color={
            item?.status.toLowerCase() === 'completed'
              ? theme.colors.green
              : item?.status.toLowerCase() === 'failed'
                ? theme.colors.red
                : item?.status.toLowerCase() === 'processing'
                  ? theme.colors.yellow
                  : theme.colors.orange
          }
        >
          &#9679; {CommonUtility.toTitleCase(item?.status) || 'N/A'}
        </ColoredPill>
      </Table.Cell>
      <Table.Cell>
        <HoverBorderButton
          loading={loading}
          onClick={() => handleDownload(item)}
          className="flex-btn"
        >
          <DownloadSimple className="mr-1" /> Download File
        </HoverBorderButton>
      </Table.Cell>
    </Table.Row>
  );
}

function getExportType(item) {
  return (
    item?.metadata?.exportType ||
    item?.metadata?.filters?.exportType ||
    (item?.results?.messages?.filename?.includes('Shipped-Orders-Export')
      ? 'shipped-orders'
      : 'waves-summary')
  );
}

function formatExportType(exportType) {
  return exportType === 'shipped-orders' ? 'Shipped Orders' : 'Wave Summary';
}
